import React from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { BarChart, Bar, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useColorMode } from '../../../../theme/ColorModeProvider';
import { getChartPalette } from '../../../../theme/colorMode';
import { Section, Panel, MetricGrid, DataTable, HiddenChartTable, LearningHubBadge, Callout, EmptyState } from './shared';
import { formatCount, formatDay } from '../utils';
import { useInsightsStyles } from '../styles';

const windowDays = key => String(key || '').replace(/d$/, '');

/** `users` section. */
const LearnersWhoAndWhere = ({ data = {}, onCustomer, onProject }) => {
  const classes = useInsightsStyles();
  const chart = getChartPalette(useColorMode().mode);
  const testAccounts = data.test_accounts || {};
  const newByDay = data.new_by_day || {};
  const daily = (newByDay.days || []).map((day, index) => ({ day, label: formatDay(day), value: newByDay.values?.[index] ?? 0 }));
  const companyColumns = [
    { key: 'customer', label: 'Company' }, { key: 'partner_label', label: 'Partner type' },
    { key: 'projects', label: 'Projects', render: row => formatCount(row.projects) }, { key: 'learners', label: 'Learners', render: row => formatCount(row.learners) },
    { key: 'active_accounts', label: 'Can sign in', render: row => formatCount(row.active_accounts) }, { key: 'with_seat', label: 'Have a seat', render: row => formatCount(row.with_seat) },
    { key: 'learning', label: 'Learning', render: row => formatCount(row.learning) },
  ];
  const projectColumns = [
    { key: 'project', label: 'Project' }, { key: 'customer', label: 'Company' }, { key: 'status', label: 'Status' },
    { key: 'learners', label: 'Learners in project', render: row => formatCount(row.learners) },
    { key: 'with_seat', label: 'Have a seat', render: row => formatCount(row.with_seat) },
    { key: 'learning', label: 'Learning', render: row => formatCount(row.learning) },
    { key: 'learning_hub', label: 'Learning Hub', render: row => <LearningHubBadge value={row.learning_hub} /> },
  ];
  return (
    <Section id="users" title="Learners" exportKey="users">
      <Panel>
        {testAccounts.excluded ? (
          <Callout info title={`${formatCount(testAccounts.excluded)} test accounts excluded`} body={testAccounts.description} />
        ) : null}
        <MetricGrid items={data.totals || []} columns={3} />
        <Grid container spacing={2} className={classes.splitCards}>
          <Grid item xs={12} md={4}>
            <Typography component="h3" className={classes.sectionTitle} style={{ marginBottom: 8 }}>New learners</Typography>
            <div style={{ display: 'grid', gap: 8 }}>
              {(data.new_learners || []).map(item => (
                <div className={classes.tile} key={item.key} style={{ minWidth: 0 }}>
                  <Typography className={classes.sectionSubtitle} style={{ marginTop: 0 }}>{item.label}</Typography>
                  <Typography style={{ lineHeight: 1.45 }}>
                    <strong style={{ fontSize: '1.25rem' }}>{formatCount(item.value)}</strong>{' '}
                    <span className={classes.muted} style={{ fontSize: '0.8125rem' }}>vs {formatCount(item.previous)} in the {windowDays(item.key)} days before</span>
                  </Typography>
                  {item.description ? <Typography className={classes.description}>{item.description}</Typography> : null}
                </div>
              ))}
            </div>
          </Grid>
          <Grid item xs={12} md={8}>
            <Typography component="h3" className={classes.sectionTitle}>New learners by day</Typography>
            {daily.length ? <>
              <div className={classes.chart} role="img" aria-label="Bar chart of learner accounts created each day">
                <ResponsiveContainer><BarChart data={daily} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}><CartesianGrid stroke={chart.grid} vertical={false} /><XAxis dataKey="label" stroke={chart.axis} tick={{ fontSize: 12 }} minTickGap={16} /><YAxis stroke={chart.axis} tick={{ fontSize: 12 }} allowDecimals={false} /><Tooltip contentStyle={{ background: chart.tooltipBg, borderColor: chart.tooltipBorder }} formatter={value => formatCount(value)} /><Bar dataKey="value" name="New learners" fill="#3D7DDD" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer>
              </div>
              <HiddenChartTable caption="New learner accounts by day" headers={['Day', 'New learners']} rows={daily.map(item => [item.label, item.value])} />
              {newByDay.description ? <Typography className={classes.description}>{newByDay.description}</Typography> : null}
            </> : <div style={{ marginTop: 8 }}><EmptyState>{newByDay.description}</EmptyState></div>}
          </Grid>
        </Grid>
        <Typography component="h3" className={classes.subheading}>Learners by company</Typography>
        <DataTable ariaLabel="Learners by company" columns={companyColumns} rows={data.by_company || []} onRowClick={onCustomer} getRowKey={row => row.customer_id} />
        <Typography component="h3" className={classes.subheading}>Projects with the most learners</Typography>
        <DataTable ariaLabel="Projects with the most learners" columns={projectColumns} rows={data.by_project || []} onRowClick={onProject} getRowKey={row => row.project_id} />
        {(data.notes || []).map((note, index) => <Typography key={index} className={classes.footnote}>{note}</Typography>)}
      </Panel>
    </Section>
  );
};

export default LearnersWhoAndWhere;
